import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, AlertTriangle, Plus, Folder, Globe, Share2, Lock, Database } from 'lucide-react';
import { knowledgeBaseService, type KnowledgeBase, type CreateKnowledgeBaseRequest, type OrphanKnowledgeBase } from '../services/knowledgeBaseService';
import { projectService, type Project } from '../services/projectService';
import { subProjectService } from '../services/subProjectService';
import { useAuth } from '../hooks/useAuth';
import PageHeader from '@/components/layout/PageHeader';
import SearchInput from '@/components/shared/SearchInput';
import EmptyState from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface SubProject { id: string; name: string; project_id: string; }

const fmt = (d: string) => new Date(d).toLocaleDateString('en-GB');

export default function KnowledgeBases() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'SysAdmin' || user?.role === 'DevOps';

  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [orphans, setOrphans] = useState<OrphanKnowledgeBase[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [subProjects, setSubProjects] = useState<SubProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateKnowledgeBaseRequest>({
    name: '', owner_id: user?.id || '', project_id: null, sub_project_id: null, description: null, visibility: 'private', kb_class: 'standard',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true); setError(null);
      const [kbsData, projectsData, spData, orphanData] = await Promise.all([
        knowledgeBaseService.getAll(),
        projectService.getProjects(),
        subProjectService.getAll(),
        isAdmin ? knowledgeBaseService.getOrphans() : Promise.resolve([] as OrphanKnowledgeBase[]),
      ]);
      setKbs(kbsData); setProjects(projectsData); setSubProjects(spData); setOrphans(orphanData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages.knowledgeBases.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const projectMap = useMemo(() => new Map(projects.map(p => [p.id, p])), [projects]);
  const subProjectMap = useMemo(() => new Map(subProjects.map(sp => [sp.id, sp])), [subProjects]);

  const resolveProject = (kb: KnowledgeBase) => {
    if (kb.project_id) return { projectId: kb.project_id, projectName: projectMap.get(kb.project_id)?.name || kb.project_name || 'Unknown' };
    if (kb.sub_project_id) {
      const sp = subProjectMap.get(kb.sub_project_id);
      if (sp) return { projectId: sp.project_id, projectName: projectMap.get(sp.project_id)?.name || kb.project_name || 'Unknown' };
    }
    return { projectId: null, projectName: kb.project_name || null };
  };

  const uniqueProjects = useMemo(() =>
    [...new Set(kbs.map(kb => resolveProject(kb).projectName).filter(Boolean))] as string[],
    [kbs, projectMap, subProjectMap]
  );
  const uniqueOwners = useMemo(() =>
    [...new Set(kbs.map(kb => kb.owner_full_name || `${kb.owner_first_name} ${kb.owner_family_name}`))],
    [kbs]
  );

  const grouped = useMemo(() => {
    let list = kbs.map(kb => ({ ...kb, _resolved: resolveProject(kb) }));
    if (search) list = list.filter(kb => kb.name.toLowerCase().includes(search.toLowerCase()));
    if (projectFilter) list = list.filter(kb => (kb._resolved.projectName || kb._resolved.projectId) === projectFilter);
    if (ownerFilter) list = list.filter(kb => (kb.owner_full_name || `${kb.owner_first_name} ${kb.owner_family_name}`) === ownerFilter);

    const unlinked: KnowledgeBase[] = [];
    const byProject = new Map<string, {
      project: Project;
      projectKBs: KnowledgeBase[];
      subProjectKBs: Map<string, { subProject: SubProject; kbs: KnowledgeBase[] }>;
    }>();

    list.forEach(kb => {
      const projectId = kb.project_id || (kb.sub_project_id ? subProjectMap.get(kb.sub_project_id)?.project_id : null);
      if (!projectId) { unlinked.push(kb); return; }
      if (!byProject.has(projectId)) {
        byProject.set(projectId, {
          project: projectMap.get(projectId) || { id: projectId, name: kb._resolved.projectName || 'Unknown' } as Project,
          projectKBs: [],
          subProjectKBs: new Map(),
        });
      }
      const grp = byProject.get(projectId)!;
      if (kb.sub_project_id) {
        if (!grp.subProjectKBs.has(kb.sub_project_id)) {
          const sp = subProjectMap.get(kb.sub_project_id);
          grp.subProjectKBs.set(kb.sub_project_id, {
            subProject: sp || { id: kb.sub_project_id, name: kb.sub_project_name || 'Unknown', project_id: projectId },
            kbs: [],
          });
        }
        grp.subProjectKBs.get(kb.sub_project_id)!.kbs.push(kb);
      } else {
        grp.projectKBs.push(kb);
      }
    });

    return { unlinked, byProject };
  }, [kbs, search, projectFilter, ownerFilter, projectMap, subProjectMap]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) { alert(t('errors.notAuthenticated')); return; }
    try {
      setCreating(true);
      await knowledgeBaseService.create({
        ...form, owner_id: user.id,
        project_id: form.project_id || undefined,
        sub_project_id: form.sub_project_id || undefined,
        description: form.description || undefined,
        visibility: form.visibility || 'private',
      });
      setDialogOpen(false);
      setForm({ name: '', owner_id: user.id, project_id: null, sub_project_id: null, description: null, visibility: 'private', kb_class: 'standard' });
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('pages.knowledgeBases.errorCreate'));
    } finally {
      setCreating(false);
    }
  };

  const filteredSubProjects = form.project_id ? subProjects.filter(sp => sp.project_id === form.project_id) : [];

  const visibilityBadge = (visibility: KnowledgeBase['visibility']) => {
    if (visibility === 'common') return <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5"><Globe size={9}/>{t('pages.knowledgeBases.visibility.common')}</span>;
    if (visibility === 'shared') return <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 py-0.5"><Share2 size={9}/>{t('pages.knowledgeBases.visibility.shared')}</span>;
    return null; // private: no badge (default, no noise)
  };

  // LKB (reference_large) gets a badge; standard (SKB) is the default and shows nothing (no noise).
  const kbClassBadge = (kbClass?: KnowledgeBase['kb_class']) => {
    if (kbClass !== 'reference_large') return null;
    return <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded px-1 py-0.5"><Database size={9}/>{t('pages.knowledgeBases.kbClassBadge.referenceLarge')}</span>;
  };

  const KBCard = ({ kb }: { kb: KnowledgeBase }) => (
    <div className="flex flex-col gap-1.5 rounded-lg border border-slate-200 bg-white p-3 hover:border-slate-400 hover:shadow-sm transition-all w-48">
      <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => navigate(`/scv2/kb/${kb.id}`)}>
        <BookOpen size={13} className="text-slate-400 shrink-0" />
        <span className="text-sm font-medium text-slate-800 truncate">{kb.name}</span>
      </div>
      <div className="text-xs text-slate-500 truncate">{kb.owner_full_name || `${kb.owner_first_name} ${kb.owner_family_name}`}</div>
      {((kb.visibility && kb.visibility !== 'private') || kb.kb_class === 'reference_large') && (
        <div className="flex flex-wrap items-center gap-1">{kbClassBadge(kb.kb_class)}{visibilityBadge(kb.visibility)}</div>
      )}
      <div className="flex items-center justify-between text-[11px] text-slate-400 border-t border-slate-100 pt-1.5 mt-auto">
        {(kb.kb_class !== 'reference_large' || isAdmin) ? (
          <span className="cursor-pointer hover:text-slate-600" onClick={() => navigate(`/scv2/kb/${kb.id}`)}>{kb.document_count} {t('pages.knowledgeBases.docs')}</span>
        ) : <span />}
        <button
          className="text-[10px] text-blue-500 hover:text-blue-700 hover:underline"
          onClick={() => navigate(`/actor-landscape/${kb.id}`)}
        >
          {t('pages.knowledgeBases.actorLandscape')}
        </button>
      </div>
    </div>
  );

  const OrphanCard = ({ orphan }: { orphan: OrphanKnowledgeBase }) => (
    <div className="flex flex-col gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3 w-48">
      <div className="flex items-center gap-1.5">
        <AlertTriangle size={13} className="text-amber-500 shrink-0" />
        <span className="text-sm font-medium text-slate-800 truncate">{orphan.kb.name}</span>
      </div>
      <div className="text-xs text-slate-500">{t('pages.knowledgeBases.orphanReason')}: {orphan.orphan_reason}</div>
      <div className="text-[11px] text-slate-400">{orphan.kb.document_count} {t('pages.knowledgeBases.docs')} · {fmt(orphan.kb.created_date)}</div>
      <div className="flex gap-1.5 pt-1">
        <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => navigate(`/scv2/kb/${orphan.kb.id}`)}>{t('pages.knowledgeBases.open')}</Button>
        <Button size="sm" variant="destructive" className="h-6 text-xs px-2" onClick={async () => {
          if (!window.confirm(t('pages.knowledgeBases.deleteOrphanConfirm', { name: orphan.kb.name, id: orphan.kb.id }))) return;
          try { await knowledgeBaseService.deleteOrphan(orphan.kb.id, true); await loadData(); }
          catch (err) { alert(err instanceof Error ? err.message : t('pages.knowledgeBases.errorDeleteOrphan')); }
        }}>{t('pages.knowledgeBases.deleteOrphan')}</Button>
      </div>
    </div>
  );

  const KBRow = ({ label, kbList }: { label?: string; kbList: KnowledgeBase[] }) => (
    <div className="space-y-1.5">
      {label && <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">{label}</p>}
      <div className="flex flex-wrap gap-2">{kbList.map(kb => <KBCard key={kb.id} kb={kb} />)}</div>
    </div>
  );

  if (loading) return (
    <>
      <PageHeader title={t('pages.knowledgeBases.title')} />
      <div className="px-6 py-4 flex flex-wrap gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-3 w-48 space-y-2">
            <Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/2" /><Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>
    </>
  );

  if (error) return (
    <>
      <PageHeader title={t('pages.knowledgeBases.title')} />
      <div className="px-6 py-8 text-sm text-red-600">{error}</div>
    </>
  );

  return (
    <>
      <PageHeader
        title={t('pages.knowledgeBases.title')}
        subtitle={`${kbs.length} knowledge base${kbs.length !== 1 ? 's' : ''}`}
        actions={
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <Plus size={14} className="mr-1" />{t('pages.knowledgeBases.createKB')}
          </Button>
        }
      />

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-6 pt-4 pb-2 flex-wrap">
        <SearchInput value={search} onChange={setSearch} placeholder="Search knowledge bases…" className="w-56" />
        <Select value={projectFilter || 'all'} onValueChange={v => setProjectFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-44 text-xs border-slate-200"><SelectValue placeholder={t('pages.knowledgeBases.allProjects')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.knowledgeBases.allProjects')}</SelectItem>
            {uniqueProjects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={ownerFilter || 'all'} onValueChange={v => setOwnerFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-40 text-xs border-slate-200"><SelectValue placeholder={t('pages.knowledgeBases.allOwners')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.knowledgeBases.allOwners')}</SelectItem>
            {uniqueOwners.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      <div className="px-6 pb-6 space-y-6">
        {kbs.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title={t('pages.knowledgeBases.noKBs')}
            description={t('pages.knowledgeBases.noKBsDescription')}
            action={{ label: t('pages.knowledgeBases.createKB'), onClick: () => setDialogOpen(true) }}
          />
        ) : (
          <>
            {isAdmin && orphans.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-xs font-semibold text-amber-600 uppercase tracking-wide">{t('pages.knowledgeBases.orphanedKBs')}</h2>
                <div className="flex flex-wrap gap-2">{orphans.map(o => <OrphanCard key={o.kb.id} orphan={o} />)}</div>
              </div>
            )}
            {grouped.unlinked.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('pages.knowledgeBases.commonBases')}</h2>
                <div className="flex flex-wrap gap-2">{grouped.unlinked.map(kb => <KBCard key={kb.id} kb={kb} />)}</div>
              </div>
            )}
            {Array.from(grouped.byProject.entries()).map(([projectId, grp]) => (
              <div key={projectId} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Folder size={14} className="text-slate-400" />
                  <h3 className="text-sm font-medium text-slate-700">{grp.project.name}</h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4">
                  <div>
                    {grp.projectKBs.length > 0
                      ? <KBRow label={t('pages.knowledgeBases.projectKB')} kbList={grp.projectKBs} />
                      : <p className="text-xs text-slate-400 italic">{t('pages.knowledgeBases.noProjectKB')}</p>
                    }
                  </div>
                  <div className="space-y-3">
                    {Array.from(grp.subProjectKBs.entries()).map(([spId, spGrp]) => (
                      <KBRow key={spId} label={`${t('pages.knowledgeBases.subProject')}: ${spGrp.subProject.name}`} kbList={spGrp.kbs} />
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {grouped.unlinked.length === 0 && grouped.byProject.size === 0 && (
              <EmptyState icon={BookOpen} title="No matching knowledge bases" description="Try a different search or filter" />
            )}
          </>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('pages.knowledgeBases.createModal.title')}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 pt-1">
            <div className="space-y-1">
              <Label>{t('pages.knowledgeBases.createModal.name')} *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required maxLength={200} placeholder={t('pages.knowledgeBases.createModal.namePlaceholder')} />
            </div>
            <div className="space-y-1">
              <Label>{t('pages.knowledgeBases.createModal.owner')}</Label>
              <Input value={user ? `${user.first_name} ${user.family_name}` : ''} disabled className="text-slate-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{t('pages.knowledgeBases.createModal.project')}</Label>
                <Select value={form.project_id || 'none'} onValueChange={v => setForm(f => ({ ...f, project_id: v === 'none' ? null : v, sub_project_id: null }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t('pages.knowledgeBases.createModal.selectProject')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('pages.knowledgeBases.createModal.selectProject')}</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{t('pages.knowledgeBases.createModal.subProject')}</Label>
                <Select value={form.sub_project_id || 'none'} onValueChange={v => setForm(f => ({ ...f, sub_project_id: v === 'none' ? null : v }))} disabled={!form.project_id}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t('pages.knowledgeBases.createModal.selectSubProject')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('pages.knowledgeBases.createModal.selectSubProject')}</SelectItem>
                    {filteredSubProjects.map(sp => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>{t('pages.knowledgeBases.createModal.description')}</Label>
              <Textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value || null }))} rows={2} maxLength={1000} placeholder={t('pages.knowledgeBases.createModal.descriptionPlaceholder')} className="resize-none" />
            </div>
            {isAdmin && (
              <div className="space-y-1">
                <Label>{t('pages.knowledgeBases.createModal.visibility')}</Label>
                <Select value={form.visibility || 'private'} onValueChange={v => setForm(f => ({ ...f, visibility: v as 'private' | 'shared' | 'common' }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private"><span className="flex items-center gap-1.5"><Lock size={11}/>{t('pages.knowledgeBases.visibility.private')}</span></SelectItem>
                    <SelectItem value="shared"><span className="flex items-center gap-1.5"><Share2 size={11}/>{t('pages.knowledgeBases.visibility.shared')}</span></SelectItem>
                    <SelectItem value="common"><span className="flex items-center gap-1.5"><Globe size={11}/>{t('pages.knowledgeBases.visibility.common')}</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {isAdmin && (
              <div className="space-y-1">
                <Label>{t('pages.knowledgeBases.createModal.kbClass')}</Label>
                <Select value={form.kb_class || 'standard'} onValueChange={v => setForm(f => ({ ...f, kb_class: v as 'standard' | 'reference_large' }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">{t('pages.knowledgeBases.createModal.kbClassStandard')}</SelectItem>
                    <SelectItem value="reference_large">{t('pages.knowledgeBases.createModal.kbClassReferenceLarge')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">{t('pages.knowledgeBases.createModal.kbClassHint')}</p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={creating}>{creating ? t('pages.knowledgeBases.createModal.creating') : t('pages.knowledgeBases.createModal.create')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

